import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Warehouse, Settings, Info, CheckCircle2, HelpCircle, Loader2, TestTube2, Monitor } from "lucide-react";
import { toast } from "sonner";
import {
  WarehouseHierarchyConfig,
  HierarchyLevel,
  DEFAULT_HIERARCHY_CONFIG,
  LEVEL_PLACEHOLDER_EXAMPLES,
  generateLocationCodePreview,
  ValidationError,
} from "@/types/warehouse-config";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useScreenMode } from "@/providers/ModeProvider";
import { useGraphQLFetch } from "@/hooks/use-graphql-fetch";

interface LocationHierarchyLevelFromAPI {
  levelID: number;
  levelNumber: number;
  displayName: string;
  codePrefix: string | null;
  isActive: boolean;
}

const GET_LOCATION_HIERARCHY_CONFIG = `
  query GetLocationHierarchyConfig {
    locationHierarchy {
      locationHierarchyConfig {
        levelID
        levelNumber
        displayName
        codePrefix
        isActive
      }
    }
  }
`;

const UPDATE_LOCATION_HIERARCHY_LEVEL = `
  mutation UpdateLocationHierarchyLevel($level: LocationHierarchyLevelInput!) {
    locationHierarchy {
      locationHierarchyConfig {
        updateLevel(level: $level) {
          levelID
          levelNumber
          displayName
          codePrefix
          isActive
        }
      }
    }
  }
`;

export function WarehouseLocationSetupPage() {
  const { mode, setMode, isDemo, isLive } = useScreenMode("warehouse-location-setup");
  const { fetchGraphQL } = useGraphQLFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
    },
    [setMode],
  );
  const getDefaultConfig = (): WarehouseHierarchyConfig => {
    return {
      ...DEFAULT_HIERARCHY_CONFIG,
      numberOfLevels: 5,
      levels: DEFAULT_HIERARCHY_CONFIG.levels.map((level) => ({
        ...level,
        isActive: true,
      })),
    };
  };

  const getInitialConfigFromLocalStorage = (): WarehouseHierarchyConfig => {
    const saved = localStorage.getItem("warehouse-hierarchy-config");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          numberOfLevels: 5,
          levels: parsed.levels.map((level: HierarchyLevel) => ({
            ...level,
            isActive: true,
          })),
        };
      } catch (e) {
        console.error("Failed to parse saved config:", e);
      }
    }
    return getDefaultConfig();
  };

  const [config, setConfig] = useState<WarehouseHierarchyConfig>(getDefaultConfig);
  const [originalConfig, setOriginalConfig] = useState<WarehouseHierarchyConfig>(getDefaultConfig);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [editingLevel, setEditingLevel] = useState<number | null>(null);

  const fetchConfigFromAPI = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchGraphQL<{
        locationHierarchy: {
          locationHierarchyConfig: LocationHierarchyLevelFromAPI[];
        };
      }>(GET_LOCATION_HIERARCHY_CONFIG);

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        toast.error("Failed to load configuration from server");
        return;
      }

      if (response.data?.locationHierarchy?.locationHierarchyConfig) {
        const apiLevels = response.data.locationHierarchy.locationHierarchyConfig;
        const levels: HierarchyLevel[] = apiLevels.map((level) => ({
          level: level.levelNumber,
          displayName: level.displayName,
          codePrefix: level.codePrefix || "",
          isActive: level.isActive,
        }));

        const newConfig: WarehouseHierarchyConfig = {
          numberOfLevels: 5,
          levels,
        };

        setConfig(newConfig);
        setOriginalConfig(newConfig);
      }
    } catch (error) {
      console.error("Failed to fetch config from API:", error);
      toast.error("Failed to load configuration from server");
    } finally {
      setIsLoading(false);
    }
  }, [fetchGraphQL]);

  useEffect(() => {
    if (isLive) {
      fetchConfigFromAPI();
    } else {
      const localConfig = getInitialConfigFromLocalStorage();
      setConfig(localConfig);
      setOriginalConfig(localConfig);
    }
  }, [isLive, fetchConfigFromAPI]);

  const handleEditLevel = (levelNumber: number) => {
    setEditingLevel(levelNumber);
  };

  const handleCancelEdit = () => {
    // Revert changes by reloading from originalConfig
    setConfig({ ...originalConfig });
    setEditingLevel(null);
  };

  const handleSaveLevel = async (levelNumber: number) => {
    const level = config.levels.find((l) => l.level === levelNumber);
    if (!level || !level.displayName.trim()) {
      toast.error("Display name is required");
      return;
    }

    const displayNames = config.levels.map((l) => l.displayName.toLowerCase().trim());
    const duplicateDisplayName =
      displayNames.filter((name) => name === level.displayName.toLowerCase().trim()).length > 1;

    if (duplicateDisplayName) {
      toast.error("Display name must be unique across all levels");
      return;
    }

    if (level.codePrefix) {
      const codePrefixes = config.levels.map((l) => l.codePrefix.toUpperCase().trim()).filter((p) => p);
      const duplicatePrefix =
        codePrefixes.filter((prefix) => prefix === level.codePrefix.toUpperCase().trim()).length > 1;

      if (duplicatePrefix) {
        toast.error("Code prefix must be unique across all levels");
        return;
      }
    }

    if (isLive) {
      setIsSaving(true);
      try {
        const response = await fetchGraphQL<{
          locationHierarchy: {
            locationHierarchyConfig: {
              updateLevel: LocationHierarchyLevelFromAPI;
            };
          };
        }>(UPDATE_LOCATION_HIERARCHY_LEVEL, {
          level: {
            levelID: levelNumber,
            levelNumber: levelNumber,
            displayName: level.displayName,
            codePrefix: level.codePrefix || null,
            isActive: level.isActive,
          },
        });

        if (response.errors) {
          console.error("GraphQL errors:", response.errors);
          toast.error("Failed to save configuration to server");
          return;
        }

        setOriginalConfig(config);
        setEditingLevel(null);
        // Dispatch event to notify other components (e.g., left menu) to refresh their hierarchy config
        window.dispatchEvent(new CustomEvent('hierarchy-config-updated'));
        toast.success(`Level ${levelNumber} updated successfully (Live)`);
      } catch (error) {
        console.error("Failed to save config to API:", error);
        toast.error("Failed to save configuration to server");
      } finally {
        setIsSaving(false);
      }
    } else {
      localStorage.setItem("warehouse-hierarchy-config", JSON.stringify(config));
      setOriginalConfig(config);
      setEditingLevel(null);
      toast.success(`Level ${levelNumber} updated successfully (Demo)`);
    }
  };

  const handleLevelChange = (levelNumber: number, field: keyof HierarchyLevel, value: string | boolean) => {
    const updatedLevels = config.levels.map((level) =>
      level.level === levelNumber ? { ...level, [field]: value } : level,
    );

    setConfig({
      ...config,
      levels: updatedLevels,
    });
  };

  const getErrorForField = (field: string): string | undefined => {
    return validationErrors.find((e) => e.field === field)?.message;
  };

  const locationPreview = generateLocationCodePreview(config);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Warehouse className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Locations</h1>
          {isDemo ? (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              Demo Mode
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Live Mode
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <TestTube2 className={`h-4 w-4 ${isDemo ? "text-yellow-600" : "text-muted-foreground"}`} />
            <Switch checked={isLive} onCheckedChange={handleModeToggle} aria-label="Toggle live mode" />
            <Monitor className={`h-4 w-4 ${isLive ? "text-green-600" : "text-muted-foreground"}`} />
          </div>
        </div>
        <p className="text-muted-foreground mt-2">
          Configure your warehouse location hierarchy and terminology
          {isDemo && " (changes saved to browser storage)"}
          {isLive && " (changes saved to database)"}
        </p>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Flexible Location Hierarchy</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Customize the terminology for your 5-level location hierarchy to match your warehouse operations.
                Changes will apply to all location creation and management interfaces throughout the system.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Location Hierarchy Configuration
          </CardTitle>
          <CardDescription>
            Customize the display names and code prefixes for each of the 5 hierarchy levels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Level Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Level Configuration</h3>
              <Badge variant="secondary">5 Levels</Badge>
            </div>

            <div className="space-y-4">
              {config.levels.map((level) => (
                <div key={level.level} className="p-4 rounded-lg border bg-card border-border">
                  <div className="space-y-4">
                    {/* Level Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="default">Level {level.level}</Badge>
                      </div>
                      {editingLevel === level.level ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleSaveLevel(level.level)} disabled={isSaving}>
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleEditLevel(level.level)}>
                          Edit
                        </Button>
                      )}
                    </div>

                    {editingLevel === level.level ? (
                      /* Edit Mode */
                      <div className="grid gap-4 md:grid-cols-2">
                        {/* Display Name */}
                        <div className="space-y-2">
                          <Label htmlFor={`level${level.level}DisplayName`} className="flex items-center gap-1">
                            Display Name
                            <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id={`level${level.level}DisplayName`}
                            value={level.displayName}
                            onChange={(e) => handleLevelChange(level.level, "displayName", e.target.value)}
                            placeholder={LEVEL_PLACEHOLDER_EXAMPLES[level.level - 1]}
                            className={getErrorForField(`level${level.level}DisplayName`) ? "border-red-500" : ""}
                          />
                          {getErrorForField(`level${level.level}DisplayName`) && (
                            <p className="text-sm text-red-500">{getErrorForField(`level${level.level}DisplayName`)}</p>
                          )}
                          <p className="text-xs text-muted-foreground">Customer-facing name for this level</p>
                        </div>

                        {/* Code Prefix */}
                        <div className="space-y-2">
                          <Label htmlFor={`level${level.level}CodePrefix`} className="flex items-center gap-2">
                            Code Prefix (Optional)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>Optional prefix for location codes. Example: "A" for Aisle, "R" for Rack</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <Input
                            id={`level${level.level}CodePrefix`}
                            value={level.codePrefix}
                            onChange={(e) => handleLevelChange(level.level, "codePrefix", e.target.value.toUpperCase())}
                            placeholder="e.g., WH, A, R"
                            maxLength={4}
                          />
                          <p className="text-xs text-muted-foreground">3-4 characters recommended</p>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="grid gap-4 md:grid-cols-2">
                        {/* Display Name */}
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Display Name</Label>
                          <div className="text-base font-medium">{level.displayName}</div>
                        </div>

                        {/* Code Prefix */}
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Code Prefix</Label>
                          <div className="text-base font-medium">
                            {level.codePrefix || <span className="text-muted-foreground italic">Not set</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Capacity Tracking - Future Implementation */}
          <div className="space-y-4 pt-4 border-t opacity-60 pointer-events-none relative">
            <div className="absolute top-0 right-0 z-10">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Future Implementation
              </Badge>
            </div>
            <h3 className="text-lg font-semibold">Capacity Management</h3>
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="space-y-1 flex-1">
                <Label htmlFor="capacityTracking" className="text-base font-medium">
                  Enable capacity tracking for locations
                </Label>
                <p className="text-sm text-muted-foreground">
                  Track maximum capacity and current utilization for each location. Disable if you don't need this
                  feature.
                </p>
              </div>
              <Switch id="capacityTracking" checked={false} disabled />
            </div>
          </div>

          {/* Location Code Preview */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">Location Code Preview</h3>
            <Card className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 flex-1">
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">Example Location Code</p>
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-2 bg-white dark:bg-gray-900 rounded border text-lg font-mono">
                        {locationPreview}
                      </code>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Based on your settings, location codes will follow this format. The numbers (01, 02, etc.) will
                      increment automatically.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
